import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { CrudFilter, HttpError } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { DataTableActionBar } from "@/components/refine-ui/data-table/data-table-action-bar";
import { ListView } from "@/components/refine-ui/views/list-view";
import { LeagueFilter } from "@/pages/matches/components/league-filter";
import { TeamFilterChip } from "@/pages/matches/components/team-filter-chip";
import { useMatchColumns } from "@/pages/matches/hooks/use-columns";
import { matchUrlFilters } from "@/pages/matches/lib/match-url-filters";
import { startOfTodayUnix } from "@/pages/matches/lib/start-of-today";
import { IMatchData } from "@/types/match";

/**
 * refine's `useTable` copies `filters.permanent` into its own state on first
 * mount, so a changed URL filter would leave the old one merged into requests.
 * Keying the table on the URL filter remounts it (and resets the page) instead.
 */
export const MatchesList = () => {
  const [searchParams] = useSearchParams();
  const leagueId = searchParams.get("leagueId");
  const teamId = searchParams.get("teamId");
  const permanent = useMemo(
    () => matchUrlFilters(searchParams),
    [searchParams.toString()],
  );

  return (
    <MatchesTable
      key={`${leagueId ?? ""}|${teamId ?? ""}`}
      permanent={permanent}
    />
  );
};

export const MatchesTable = ({ permanent }: { permanent: CrudFilter[] }) => {
  const columns = useMatchColumns();

  const table = useTable<IMatchData, HttpError>({
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    initialState: {
      columnVisibility: { id: false },
      columnPinning: { right: ["actions"] },
    },
    refineCoreProps: {
      meta: { defaultQueryBy: "homeTeam.name,awayTeam.name,league.name" },
      sorters: { initial: [{ field: "startDate", order: "asc" }] },
      filters: {
        permanent,
        // Open on upcoming matches; the Kickoff column filter can clear it.
        initial: [
          { field: "startDate", operator: "gte", value: startOfTodayUnix() },
        ],
      },
      pagination: { pageSize: 25 },
    },
  });

  return (
    <ListView>
      <div className="flex flex-wrap items-end gap-2">
        <LeagueFilter />
        <TeamFilterChip />
      </div>
      <DataTableActionBar resource="matches" table={table} />
      <DataTable table={table} />
    </ListView>
  );
};
